import { computed, reactive, ref, watch } from 'vue';
import { useRichTextEditor } from './useTiptapEditor';
import { useEditorOptions } from './useEditorOptions';
import { editors, getEditor, getEditorState, isEditorRTL } from './editorsMap';
import { fontSizeOptions, onSelectionUpdate } from './utils';

import {
  AlignRight,
  AlignLeft,
  PilcrowLeft,
  PilcrowRight,
} from 'lucide-vue-next';

export function useEditor() {
  const currentId = ref();

  const createEditor = ({
    id,
    model,
    editable,
    placeholder = 'לכתוב משהו כאן',
    onUpdate,
    onFocus = () => {},
    onBlur = () => {},
    onEnter = () => {},
    onPaste = () => {},
    extensions = {},
    autoFocus = false,
    keyboard = true,
    direction = 'rtl',
  }) => {
    currentId.value = id;
    // Ensure the placeholder is applied if no custom configuration is provided
    const state = getEditorState(id);
    //@ts-ignore
    if (extensions.placeholder === undefined && placeholder) {
      //@ts-ignore
      extensions.placeholder = { placeholder };
    }
    editors.set(id, {
      direction,
      enables: reactive(extensions),
      editor: useRichTextEditor({
        editable,
        model,
        onUpdate,
        keyboard,
        //@ts-ignore
        onSelectionUpdate,
        autoFocus,
        onFocus,
        onBlur,
        onEnter,
        onPaste,
        extensions,
      }),
    });

    state.alignmentRef.value = isEditorRTL(id) ? AlignRight : AlignLeft;
    state.directionRef.value = isEditorRTL(id) ? PilcrowLeft : PilcrowRight;
  };

  const setCurrentId = id => (currentId.value = id);

  const getFontSizeOptions = computed(() => {
    const state = getEditorState(currentId.value);
    const currentSelection =
      state.activeTypo.value?.attributes?.class ?? 'body';
    return fontSizeOptions.filter(option =>
      option.applyTo.includes(currentSelection),
    );
  });

  const optionsGroups = ref();

  watch(
    [
      () => getEditorState(currentId.value),
      getFontSizeOptions,
      () => editors.get(currentId.value)?.enables,
    ],
    ([state, font]) => {
      if (!currentId.value || !state) return;

      optionsGroups.value = useEditorOptions(
        currentId.value,
        state.alignmentRef,
        state.directionRef,
        state.typographyRef,
        () => getFontSizeOptions.value,
      );
    },
    { immediate: true, deep: true },
  );

  return {
    setCurrentId,
    createEditor,
    getEditor,
    optionsGroups,
  };
}
